<template>
  <div>
    <DemoNotice v-if="isDemo" />

    <div class="mb-6">
      <h1 class="text-2xl font-bold text-gray-800 dark:text-gray-100">套餐销售配置</h1>
      <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">配置您的套餐售价、上架状态和展示顺序。留空售价将使用平台原价。</p>
    </div>

    <div v-if="loading" class="flex items-center justify-center py-12">
      <div class="h-8 w-8 animate-spin rounded-full border-4 border-indigo-600 border-t-transparent"></div>
    </div>

    <div v-else>
      <!-- 按套餐分组展示 -->
      <div v-for="group in groupedConfigs" :key="group.planId" class="mb-6 overflow-hidden rounded-lg bg-white shadow dark:bg-gray-800">
        <button
          type="button"
          class="flex w-full items-center justify-between border-b border-gray-200 bg-gray-50 px-4 py-3 text-left dark:border-gray-700 dark:bg-gray-900/60"
          @click="togglePlan(group.planId)"
        >
          <div>
            <h2 class="text-lg font-semibold text-gray-900 dark:text-gray-100">{{ group.planName }}</h2>
            <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {{ group.totalCount }} 个价格方案，{{ group.visibleCount }} 个已上架
            </p>
          </div>
          <span class="text-sm text-gray-500 dark:text-gray-400">
            {{ isPlanExpanded(group.planId) ? '收起' : '展开' }}
          </span>
        </button>

        <div v-if="isPlanExpanded(group.planId)" class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead class="bg-gray-50 dark:bg-gray-900/40">
              <tr>
                <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">价格方案</th>
                <th class="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">平台原价</th>
                <th class="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">结算底价</th>
                <th class="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  自定义售价 (元)
                  <div class="mt-0.5 text-[10px] font-normal normal-case text-gray-400 dark:text-gray-500">留空继承平台价</div>
                </th>
                <th class="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">上架</th>
                <th class="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">展示顺序</th>
                <th class="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">操作</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
              <tr v-for="item in group.items" :key="item.pricingId" class="hover:bg-gray-50 dark:hover:bg-gray-900/40">
                <td class="px-4 py-4 text-sm">
                  <div class="font-medium text-gray-900 dark:text-gray-100">{{ item.pricingName }}</div>
                  <div class="text-xs text-gray-500 dark:text-gray-400">
                    {{ item.durationDays }}天 · ID: {{ item.pricingId }}
                    <span v-if="!item.configId" class="ml-2 text-blue-600 dark:text-blue-400">(继承平台)</span>
                  </div>
                </td>
                <td class="px-4 py-4 text-right text-sm text-gray-500 dark:text-gray-400">
                  {{ formatCurrency(item.pricingPrice) }}
                </td>
                <td class="px-4 py-4 text-right text-sm font-medium text-gray-700 dark:text-gray-300">
                  {{ formatCurrency(item.settlementPrice) }}
                </td>
                <td class="px-4 py-4 text-right text-sm">
                  <div class="relative ml-auto w-32 rounded-md shadow-sm">
                    <div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <span class="text-gray-500 dark:text-gray-400 sm:text-sm">¥</span>
                    </div>
                    <input
                      type="number"
                      step="0.01"
                      :min="item.settlementPrice / 100"
                      :value="item.customPrice !== null ? (item.customPrice / 100).toFixed(2) : ''"
                      :placeholder="(item.pricingPrice / 100).toFixed(2)"
                      class="block w-full rounded-md border-gray-300 pl-7 text-right shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200"
                      @input="handlePriceInput(item, $event)"
                      @change="markChanged(item.pricingId)"
                    />
                  </div>
                  <div v-if="hasItemValidationError(item)" class="mt-1 text-xs text-red-600 dark:text-red-400">
                    不能低于结算底价
                  </div>
                </td>
                <td class="px-4 py-4 text-center">
                  <input
                    v-model="item.isVisible"
                    type="checkbox"
                    class="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700"
                    @change="markChanged(item.pricingId)"
                  />
                </td>
                <td class="px-4 py-4 text-center">
                  <input
                    v-model.number="item.displayOrder"
                    type="number"
                    min="0"
                    class="w-20 rounded-md border-gray-300 text-center shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200"
                    @change="markChanged(item.pricingId)"
                  />
                </td>
                <td class="px-4 py-4 text-center">
                  <button
                    v-if="item.configId"
                    @click="resetToDefault(item)"
                    class="text-sm text-gray-600 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
                    title="恢复为继承平台默认配置"
                  >
                    重置
                  </button>
                  <span v-else class="text-xs text-gray-400">-</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="sticky bottom-0 rounded-lg bg-white px-4 py-3 shadow dark:bg-gray-800">
        <button
          @click="saveChanges"
          :disabled="saving || changedPricingIds.size === 0 || hasValidationError"
          class="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {{ saving ? '保存中...' : '保存配置' }}
        </button>
        <span v-if="changedPricingIds.size > 0" class="ml-3 text-sm text-gray-600 dark:text-gray-400">
          {{ changedPricingIds.size }} 项待保存
        </span>
        <span v-if="hasValidationError" class="ml-3 text-sm text-red-600 dark:text-red-400">
          存在低于结算底价的售价，无法保存
        </span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, computed } from 'vue';
import { getTenantPricingConfigsAPI, upsertTenantPricingConfigAPI, deleteTenantPricingConfigAPI } from '@/api/tenantConsole';
import DemoNotice from '@/components/DemoNotice.vue';
import { useAuthStore } from '@/stores/auth';
import type { TenantPricingMergedItem } from '@/types/tenantConsole';
import toast from '@/utils/toaster';

const authStore = useAuthStore();
const isDemo = computed(() => authStore.isDemo);

interface PlanGroup {
  planId: number;
  planName: string;
  items: TenantPricingMergedItem[];
  totalCount: number;
  visibleCount: number;
}

const items = ref<TenantPricingMergedItem[]>([]);
const loading = ref(false);
const saving = ref(false);
const changedPricingIds = ref(new Set<number>());
const expandedPlanIds = ref(new Set<number>());

const formatCurrency = (cents: number | null | undefined): string => {
  if (typeof cents !== 'number' || !Number.isFinite(cents)) {
    return '--';
  }
  return `¥${(cents / 100).toFixed(2)}`;
};

const normalizeCustomPrice = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  return null;
};

const hasItemValidationError = (item: TenantPricingMergedItem): boolean => {
  const customPrice = normalizeCustomPrice(item.customPrice);
  return customPrice !== null && customPrice < item.settlementPrice;
};

const handlePriceInput = (item: TenantPricingMergedItem, event: Event) => {
  const target = event.target as HTMLInputElement;
  const value = target.value;
  if (value === '' || value === null || value === undefined) {
    item.customPrice = null;
  } else {
    const parsed = parseFloat(value);
    if (!isNaN(parsed)) {
      item.customPrice = Math.round(parsed * 100);
    }
  }
};

const groupedConfigs = computed<PlanGroup[]>(() => {
  const groups = new Map<number, PlanGroup>();
  
  for (const item of items.value) {
    // 不显示免费价格方案，租户无需也无法配置免费套餐的销售
    if (item.pricingPrice === 0) continue;

    if (!groups.has(item.planId)) {
      groups.set(item.planId, {
        planId: item.planId,
        planName: item.planName || '未命名套餐',
        items: [],
        totalCount: 0,
        visibleCount: 0,
      });
    }
    const group = groups.get(item.planId)!;
    group.items.push(item);
    group.totalCount += 1;
    if (item.isVisible) {
      group.visibleCount += 1;
    }
  }
  
  return Array.from(groups.values()).map((group) => ({
    ...group,
    items: [...group.items].sort((a, b) => a.displayOrder - b.displayOrder),
  }));
});

const hasValidationError = computed(() => {
  return items.value.some((item) =>
    changedPricingIds.value.has(item.pricingId) && hasItemValidationError(item),
  );
});

const togglePlan = (planId: number) => {
  if (expandedPlanIds.value.has(planId)) {
    expandedPlanIds.value.delete(planId);
    return;
  }
  expandedPlanIds.value.add(planId);
};

const isPlanExpanded = (planId: number) => expandedPlanIds.value.has(planId);

const markChanged = (pricingId: number) => {
  changedPricingIds.value.add(pricingId);
};

const loadConfigs = async () => {
  loading.value = true;
  try {
    const res = await getTenantPricingConfigsAPI();
    if (res.status === 200 && res.data) {
      items.value = res.data.items;
      expandedPlanIds.value = new Set(items.value.map((item) => item.planId));
    }
  } catch (error) {
    toast.error('加载配置失败');
    console.error(error);
  } finally {
    loading.value = false;
  }
};

const resetToDefault = async (item: TenantPricingMergedItem) => {
  if (!item.configId) {
    return;
  }

  if (!confirm(`确定要将"${item.pricingName}"恢复为继承平台默认配置吗？`)) {
    return;
  }

  try {
    const res = await deleteTenantPricingConfigAPI(item.configId);
    if (res.status === 200) {
      toast.success('已恢复为继承平台默认配置');
      await loadConfigs();
      changedPricingIds.value.delete(item.pricingId);
    } else {
      toast.error(res.message || '操作失败');
    }
  } catch (error) {
    toast.error('操作失败');
    console.error(error);
  }
};

const saveChanges = async () => {
  // 验证
  for (const item of items.value) {
    if (changedPricingIds.value.has(item.pricingId)) {
      if (hasItemValidationError(item)) {
        toast.error(`价格方案"${item.pricingName}"的售价不能低于结算底价`);
        return;
      }
    }
  }

  saving.value = true;
  try {
    const promises = Array.from(changedPricingIds.value).map((pricingId) => {
      const item = items.value.find((i) => i.pricingId === pricingId);
      if (!item) return Promise.resolve();
      
      return upsertTenantPricingConfigAPI(pricingId, {
        customPrice: normalizeCustomPrice(item.customPrice),
        isVisible: item.isVisible,
        displayOrder: item.displayOrder,
      });
    });

    await Promise.all(promises);
    toast.success('保存成功');
    changedPricingIds.value.clear();
    await loadConfigs();
  } catch (error) {
    toast.error('保存失败');
    console.error(error);
  } finally {
    saving.value = false;
  }
};

onMounted(() => {
  loadConfigs();
});
</script>
